// src/contexts/AudioPlayingContext.tsx

import React, {
  createContext,
  useState,
  useRef,
  useEffect,
  useCallback,
} from "react";
import { gcpTextToSpeech } from "../api/gcp";
import { useLocation } from "react-router-dom";

interface AudioPlayingContextType {
  isAudioPlaying: boolean;
  playAudio: (src: string) => void;
  stopAudio: () => void;
  pauseAudio: () => void;
  onAudioDisrubted: () => void;
  audioRef: React.MutableRefObject<HTMLAudioElement | null>;
  isAudioPlayingRef: React.MutableRefObject<boolean>;
  // Add new methods for direct audio element control
  controlAudio: (id: string, element: HTMLAudioElement) => void;
  stopControlledAudio: () => void;
  currentControlledId: string | null;
}

export const AudioPlayingContext = createContext<
  AudioPlayingContextType | undefined
>(undefined);

export const AudioPlayingProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [isAudioPlaying, setIsAudioPlaying] = useState(false);
  const [queue, setQueue] = useState<string[]>([]); // <-- Our audio queue
  const isAudioPlayingRef = useRef(isAudioPlaying);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const location = useLocation();

  // New state for controlled audio elements
  const [currentControlledId, setCurrentControlledId] = useState<string | null>(
    null
  );
  const controlledAudioElements = useRef<Map<string, HTMLAudioElement>>(
    new Map()
  );

  // Function to check if current route allows audio
  const isAllowedRoute = useCallback(() => {
    return (
      location.pathname.includes("/mock-case") ||
      location.pathname.includes("/report") ||
      location.pathname.includes("/case-mock")
    );
  }, [location.pathname]);

  /**
   * Attempts to play the first queued item if no audio is currently playing.
   */
  const processQueue = useCallback(() => {
    // If we're on a route that doesn't allow audio, don't play anything
    if (!isAllowedRoute()) {
      return;
    }

    // If there's something playing, a controlled audio is playing, or the queue is empty, do nothing
    if (audioRef.current || currentControlledId || queue.length === 0) {
      return;
    }

    // Get the first item in the queue
    const src = queue[0];
    const audio = new Audio(src);
    audioRef.current = audio;

    audio.onplaying = () => {
      console.log("Playing audio:", src);
      setIsAudioPlaying(true);
      isAudioPlayingRef.current = true;
    };

    audio.onpause = () => {
      console.log("Paused audio:", src);
      setIsAudioPlaying(false);
      isAudioPlayingRef.current = false;
    };

    audio.onended = () => {
      console.log("Audio ended:", src);
      setIsAudioPlaying(false);
      isAudioPlayingRef.current = false;
      audioRef.current = null;

      // Remove the just-finished source from the queue
      setQueue((prevQueue) => prevQueue.slice(1));
    };

    audio.onloadedmetadata = () => {
      const duration = audio.duration;
      console.log(`Audio duration for ${src}: ${duration} seconds`);
    };

    // Start playing
    audio.play().catch((e) => {
      console.error("Error playing audio:", e);
    });
  }, [queue, isAllowedRoute, currentControlledId]);

  /**
   * Whenever the queue changes or route changes, attempt to process the next item.
   */
  useEffect(() => {
    // If not allowed route, stop any playing audio
    if (!isAllowedRoute()) {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
        setIsAudioPlaying(false);
        isAudioPlayingRef.current = false;
      }

      // Also stop any controlled audio
      if (currentControlledId) {
        stopControlledAudio();
      }

      return;
    }

    // If no audio is playing and no controlled audio is playing, try to process the first queued item
    if (!audioRef.current && !currentControlledId) {
      processQueue();
    }
  }, [queue, processQueue, isAllowedRoute, currentControlledId]);

  // Watch for route changes to stop audio when navigating to restricted routes
  useEffect(() => {
    if (!isAllowedRoute()) {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
        setIsAudioPlaying(false);
        isAudioPlayingRef.current = false;
        console.log("Audio stopped due to navigating to restricted route");
      }

      // Also stop any controlled audio
      if (currentControlledId) {
        stopControlledAudio();
      }
    }
  }, [location.pathname, isAllowedRoute, currentControlledId]);

  /**
   * Register and control an audio element directly.
   * This is used for the Recording component.
   */
  const controlAudio = (id: string, element: HTMLAudioElement) => {
    // Register the element
    controlledAudioElements.current.set(id, element);

    // If we try to play this audio and another one is playing, stop all other audio
    if (id !== currentControlledId) {
      // Stop any controlled audio
      if (
        currentControlledId &&
        controlledAudioElements.current.has(currentControlledId)
      ) {
        const currentElement =
          controlledAudioElements.current.get(currentControlledId)!;
        currentElement.pause();
        currentElement.currentTime = 0;
      }

      // Stop any queued audio
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
        setIsAudioPlaying(false);
        isAudioPlayingRef.current = false;
      }

      // Set the new controlled audio
      setCurrentControlledId(id);

      // Start playing
      element.play().catch((e) => {
        console.error("Error playing controlled audio:", e);
        setCurrentControlledId(null);
      });
    }
  };

  /**
   * Stop the currently controlled audio.
   */
  const stopControlledAudio = () => {
    if (
      currentControlledId &&
      controlledAudioElements.current.has(currentControlledId)
    ) {
      const element = controlledAudioElements.current.get(currentControlledId)!;
      element.pause();
      element.currentTime = 0;
    }
    setCurrentControlledId(null);
  };

  /**
   * Queues up a new audio file. If no audio is currently playing,
   * processQueue() will start it.
   */
  const playAudio = (src: string) => {
    // Stop any controlled audio first
    if (currentControlledId) {
      stopControlledAudio();
    }

    setQueue((prev) => [...prev, src]);
  };

  /**
   * Pauses the currently playing audio (if any).
   */
  const pauseAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
    }

    // Also pause any controlled audio
    if (
      currentControlledId &&
      controlledAudioElements.current.has(currentControlledId)
    ) {
      controlledAudioElements.current.get(currentControlledId)!.pause();
    }
  };

  /**
   * Stops audio and clears the current queue.
   */
  const stopAudio = () => {
    console.log("Stopping audio:", audioRef.current);

    if (audioRef.current) {
      console.log("...Stopping current audio");
      audioRef.current.pause();
      audioRef.current = null;
      setIsAudioPlaying(false);
      isAudioPlayingRef.current = false;
    }

    // Also stop any controlled audio
    if (currentControlledId) {
      stopControlledAudio();
    }

    // Clear queue so nothing else starts
    setQueue([]);
  };

  /**
   * Example "disruption" handler for your custom TTS prompt,
   * if you want to interrupt the current audio and play the TTS message.
   */
  const onAudioDisrubted = () => {
    // For example:
    // setTimeout(async () => {
    //   if (isAudioPlayingRef.current) {
    //     pauseAudio();
    //     const audioBlob = await gcpTextToSpeech(
    //       "Hey umm don't talk over the audio!",
    //       process.env.REACT_APP_GCP_TTS_API_KEY || ""
    //     );
    //     const audioUrl = URL.createObjectURL(audioBlob);
    //     // Insert the TTS message at the front of the queue OR just play it right away
    //     setQueue((prev) => [audioUrl, ...prev]);
    //   }
    // }, 700);
  };

  return (
    <AudioPlayingContext.Provider
      value={{
        isAudioPlaying,
        playAudio,
        stopAudio,
        pauseAudio,
        audioRef,
        isAudioPlayingRef,
        onAudioDisrubted,
        controlAudio,
        stopControlledAudio,
        currentControlledId,
      }}
    >
      {children}
    </AudioPlayingContext.Provider>
  );
};
